import { defineField, defineGroup } from '@/form/useForm'
import type { UseAssetFormComputedData, UseAssetFormState } from '../types'
import {
  EAssetAutomationLevel,
  EAssetSource,
  EAssetType,
  evaluateAssetAutomationLevel,
  type ILedger,
  type IProviderImport
} from 'findef'
import { match } from '@/utils/etc'
import { lockedByIr, lockedByProvider, lockedByTicker } from '../toolips'
import { useUserSettings } from '@/modules/settings/hooks/useUserSettings'
import { getCurrencyOptions } from '@/data/currencies'
import Icon from 'components/icon'
import { twColor } from '@/utils/style'
import { Grid } from 'components/grid'

const CURRENCIES = getCurrencyOptions()

export const ledgerField = () => {
  const settings = useUserSettings()
  return defineGroup<UseAssetFormState, ILedger, UseAssetFormComputedData>({
    fields: {
      sharePrice: defineGroup({
        attributes: {
          class: 'grid grid-cols-[auto_7rem]',
          label: 'Latest Share Price',
          needs: ['asset.type'],
          next: (state) => ({
            label: match(
              state.asset.type,
              [
                [EAssetType.CRYPTO, `Latest Coin Price`],
                [EAssetType.EQUITY, `Latest Share Price`],
                [EAssetType.REAL_ESTATE, `Current Property Value`],
                [EAssetType.COMMODITY, `Latest Unit Price`],
                [EAssetType.ALTERNATIVE, `Latest Unit Price`]
              ],
              'Latest Share Price'
            ),
            // Only for Real Estate
            tooltip: state.asset.type === EAssetType.REAL_ESTATE ? `The current total property value` : undefined
          })
        },
        fields: {
          value: defineField({
            type: 'number',
            value: 0,
            placeholder: '0',
            needs: [
              'asset.type',
              'asset.cryptoQuote',
              'asset.commodityQuote',
              'asset.companyProfile',
              'asset.source',
              'asset.listed',
              'manualTicker',
              'providerImport',
              'currentValue'
            ],
            next: (state) => {
              const providerImport = state.asset.providerImport as
                | IProviderImport
                | null
                | undefined
              const crypto = state.asset.cryptoQuote
              const commodity = state.asset.commodityQuote
              const company = state.asset.companyProfile
              return {
                value: (() => {
                  if (state.manualTicker || (state.asset.listed !== true) && !(state.asset.cryptoQuote && state.asset.cryptoQuote.manuallyAdded !== true)) {
                    return state.asset.ledger.sharePrice.value
                  }
                  if (providerImport && state.currentValue) {
                    return state.asset.ledger.sharePrice.value;
                  }
                  if (crypto) return crypto.avgPrice
                  if (company && !company.manuallyAdded && company.price) return company.price
                  if (commodity && !commodity.manuallyAdded && commodity.price) return commodity.price
                  return state.asset.ledger.sharePrice.value;
                })(),
                locked:
                  evaluateAssetAutomationLevel(state.asset) ===
                    EAssetAutomationLevel.SEMI_AUTOMATIC ||
                  state.asset.source === EAssetSource.IR ||
                  !!state.providerImport,
                lockedReason: 'This field is maintained by another source',
                ...(state.asset.source === EAssetSource.IR
                  ? lockedByIr(state)
                  : providerImport
                    ? lockedByProvider(state) : (crypto || commodity || company) ? lockedByTicker(state) : { lockedReasonBubbletip: undefined })
              }
            }
          }),
          type: defineField({
            type: 'text',
            value: settings.currency.value,
            component: 'selectbox',
            injectedProps: {
              compact: true,
              invalidValuePlaceholder: {
                body: () => <Grid noDefaultLayout columns="max-content max-content" alignItems="center" justifyContent="center" gap="0.5rem">
                  <div>
                    <Icon src={async () => import('assets/img/svg/icons/warning_hex.svg?raw')} size="1.25rem" fill={twColor('gray-500')} stroke="none"/>
                  </div>
                  <div class="text-gray-500 font-medium">
                    N/A
                  </div>
                </Grid>
              }
            },
            options: {
              items: CURRENCIES
            },
            needs: [
              'asset.cryptoQuote',
              'asset.commodityQuote',
              'asset.companyProfile',
              'asset.source',
              'asset.listed',
              'invested.type',
              'providerImport',
              'manualTicker'
            ],
            next: (state) => {
              if (state.manualTicker || (state.asset.listed !== true && !(state.asset.cryptoQuote && state.asset.cryptoQuote.manuallyAdded !== true))) {
                return {
                  locked: true,
                  lockedReasonBubbletip: undefined,
                  value: state.invested.type || settings.currency.value
                }
              }
              const providerImport = state.asset.providerImport as
                | IProviderImport
                | null
                | undefined
              const isInvalid =
                state.asset.ledger.sharePrice.type &&
                !CURRENCIES.find((it) => it.value === state.asset.ledger.sharePrice.type)
              const locked =
                evaluateAssetAutomationLevel(state.asset) ===
                  EAssetAutomationLevel.SEMI_AUTOMATIC ||
                state.asset.source === EAssetSource.IR ||
                !!state.providerImport
              const crypto = state.asset.cryptoQuote
              const company = state.asset.companyProfile
              const commodity = state.asset.commodityQuote
              return {
                value: (() => {
                  if (providerImport && state.currentValue) {
                    return (
                      state.asset.ledger.sharePrice.type || state.asset.currency || settings.currency.value
                    )
                  }
                  if (crypto) return crypto.tickerTo || settings.currency.value
                  if (company) return company.currency || settings.currency.value
                  if (commodity && !commodity.manuallyAdded) return 'USD'
                  return state.invested.type || settings.currency.value
                })(),
                locked:
                  evaluateAssetAutomationLevel(state.asset) ===
                    EAssetAutomationLevel.SEMI_AUTOMATIC ||
                  state.asset.source === EAssetSource.IR ||
                  !!state.providerImport || (state.asset.commodityQuote && state.asset.commodityQuote.manuallyAdded === true),

                ...(isInvalid && locked
                  ? {
                      lockedReasonBubbletip: {
                        text: 'We dont have support for this currency',
                        variant: 'white'
                      }
                    }
                  : {
                      lockedReason: 'This field is maintained by another source',
                      ...(state.asset.source === EAssetSource.IR
                        ? lockedByIr(state)
                        : providerImport
                          ? lockedByProvider(state) : (crypto || commodity || company) ? lockedByTicker(state)
                          : { lockedReasonBubbletip: undefined })
                    })
              }
            }
          })
        }
      }),
      sharesIssued: defineField({ type: 'number', value: 0 })
    }
  })
}
